// Public privacy notice for dialog respondents, linked from the dialog chat
// strip and the invite email footer. Static by design: no auth and no data
// fetching, so a respondent can read it before deciding to take part.

const linkClass = 'underline hover:text-foreground';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-linear-to-b from-background to-muted">
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8 text-sm leading-relaxed text-muted-foreground">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">How we handle your dialog data</h1>
          <p className="text-xs">Last updated: 30 July 2026</p>
        </header>

        <Section title="Who we are">
          <p>
            ImpactML Ltd builds ValueGraph, the tool running the conversation you were invited to.
            We're the data controller for everything described on this page. For anything about
            your data, email{' '}
            <a href="mailto:hello@impactml.io" className={linkClass}>
              hello@impactml.io
            </a>
            .
          </p>
        </Section>

        <Section title="What we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              If your invitation came by email, the address it was sent to. Shared links don't
              give us your email.
            </li>
            <li>
              Your role, team and company, if the person who invited you added them to give the
              conversation context.
            </li>
            <li>The conversation itself: your messages and the assistant's replies.</li>
            <li>
              The insights shown on the review screen. Approving them there is how you confirm
              what goes into the research. If you leave without finishing, the team can still
              see the notes captured so far.
            </li>
            <li>
              Basic usage events (page loaded, conversation started, completed) tied to a random
              session id, plus a coarse location (country and city) derived from your connection.
              No cookies, no advertising identifiers.
            </li>
          </ul>
        </Section>

        <Section title="What we use it for">
          <p>
            Product research. Your approved insights help the team that invited you decide what to
            build. We also use conversations to improve how the assistant runs interviews. We don't
            sell your data, we don't use it for advertising, and you won't end up on a mailing
            list.
          </p>
        </Section>

        <Section title="The AI part">
          <p>
            The interview is run by an AI model hosted on Amazon Web Services. Your messages go to
            that model so it can reply, and that processing happens in AWS's European regions. We
            also keep scrubbed traces of conversations (emails and access links removed) in a
            monitoring tool we use to check the assistant is behaving well; those traces are
            processed in the United States.
          </p>
        </Section>

        <Section title="Where it lives and for how long">
          <p>
            Everything except those traces is stored in AWS's London region. How long we keep it:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The raw conversation transcript is deleted automatically after 7 days.</li>
            <li>
              Approved insights, your email address, and the role, team and company details are
              kept while the research project is active.
            </li>
            <li>
              Usage events are kept for 30 days; error reports (scrubbed of emails and links) for 90
              days; the scrubbed AI traces for up to 400 days.
            </li>
            <li>
              If you leave a conversation without submitting, the notes captured so far may still
              be visible to the project team. Conversations are kept for 7 days and then deleted.
            </li>
          </ul>
        </Section>

        <Section title="Your rights">
          <p>
            Taking part is voluntary and you can stop at any point. What you approve on the review
            screen is what joins the research; if you stop partway, the notes captured so far may
            still be visible to the inviting team for up to 7 days. You can ask us at any time to show you
            what we hold about you, correct it, or delete all of it. Email{' '}
            <a href="mailto:hello@impactml.io" className={linkClass}>
              hello@impactml.io
            </a>{' '}
            and we'll do it within a month, usually much faster. If you think we've handled your
            data badly, you can complain to the UK Information Commissioner's Office (
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              ico.org.uk
            </a>
            ).
          </p>
        </Section>
      </div>
    </div>
  );
}
